/**
 * Which steps may bring their API phase into existence, and which may only read
 * one that is already there.
 *
 * Several hub steps share a phase with another step — `STEP_TO_PHASE_TYPE` in
 * `usePhases.ts` maps `stimulation` and `trigger_shot` onto `protocol`,
 * `pgt_testing` onto `embryo_development`, `outcome` onto `luteal_phase`. Most
 * of them own something on that phase and may create it. `outcome` does not: it
 * borrows the luteal phase purely to read its dates, because the API models no
 * outcome phase and the result lives on the cycle's outcome record instead.
 *
 * Letting it create that row is what made this worth a module. Saved from the
 * outcome step with no luteal phase yet, the row is dated *today* rather than
 * from the transfer — and a luteal phase that predates the transfer permanently
 * fails the "luteal started after the procedure" guard in `isInWaitingPhase`,
 * so the two-week wait never appears for that cycle again.
 *
 * The step config withholds the button that did this (`derivedProgress`). This
 * is the half that does not depend on the UI staying that way, and it lives
 * here rather than beside the mapping because `usePhases.ts` reaches the API
 * client and so cannot be asserted directly.
 */

/**
 * A deny-list, deliberately. A step absent from here keeps the long-standing
 * behaviour, so this cannot silently start dropping writes for steps nobody
 * considered — only the ones named have anything taken away.
 */
const STEPS_THAT_MAY_NOT_CREATE_A_PHASE = new Set(["outcome"]);

/**
 * Whether saving this step may create its phase, or may only update one that
 * already exists.
 */
export function stepMayCreatePhase(stepId: string): boolean {
  return !STEPS_THAT_MAY_NOT_CREATE_A_PHASE.has(stepId);
}
